import React, { useMemo } from 'react';
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { startOfWeek, endOfWeek, textfieldDateFormat } from "../../../utils/dateFunctions";
import { getAPIDataReturn, getAPIDataParamsReturn, postApi, postApiOrThrow, getAPIData, getAPIDataParams } from "../../../utils/apiFunction";
import { numberFormatwithNeg } from "../../../utils/numberFunctions";
import OystersGroup from "../../../components/OysterGroup";
import Div from "@jumbo/shared/Div";
import { adalApiFetch } from '../../../../adalConfig';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import DropZone from "../../../components/DropZone";
import {v4 as uuidv4} from 'uuid';
import SelectTextField from "../../../components/SelectTextField";
import { GetAllCacheData, addToCache } from "../../../utils/cacheFunctions";
import ControlledTextField from "../../../components/ControlledTextField";
import DriverSelect from "../../../components/DriverEntry";
import SubmitButtonDisplayer from "../../../components/SubmitButtonDisplayer";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import StaffMember from "../../../components/StaffMember";
import { ACCESS, ACCESS_SECTIONS } from '../../../utils/constants/access';
import { checkAccess } from '../../../utils/roleFunctions';
import { number } from 'prop-types';
import FormModal from '../../../components/FormModal';
import DeleteCurrentRun from '../../../components/DeleteCurrentRun';


const ReceivalForm = (props) => {
    const uri = process.env.REACT_APP_BASE_URL;
    const hasStarted = !(props.selectedLine?.processingStatus === 'Not started');
    const [completed, setCompleted] = React.useState(props.completed);
    const [updateComplete, setUpdateCompleted] = React.useState(false);
    const [edit, setEdit] = React.useState(props.edit);
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [editInput, setEditInput] = React.useState(false);
    const [oystersOut, setOystersOut] = React.useState([{}]);
    const [currentOutputs, setCurrentOutputs] = React.useState(null);
    const [staffMember, setStaffMember] = React.useState(null);
    const [stockItems, setStockItems] = React.useState(null);
    const [scheduleList, setScheduleList] = React.useState(props.scheduleList);
    const [showDelete, setShowDelete] = React.useState(false);
    const [editBench, setEditBench] = React.useState(null);
    var recData = props.data ? props.data : {};
    const Swal = useSwalWrapper();
    const [equipment, setEquipment] = React.useState(null);
    const [equipmentList, setEquipmentList] = React.useState([]);
    const requiredEditAccess = { key: ACCESS_SECTIONS.PROCESSING, value: ACCESS.EDIT };
    const requiredAdminAccess = { key: ACCESS_SECTIONS.PROCESSING, value: ACCESS.ADMIN };
    const requiredMYOBAccess = { key: ACCESS_SECTIONS.PROCESSING, value: ACCESS.MYOB };
    const CompletedAndEdit = edit && completed;
    const CompletedSoDisabled = !edit && completed;
    const initialInputStockItemId = props.selectedLine?.stockItemId;
    // Build initial oystersIn, expanding mixed groups to underlying items when present
    const buildInitialInputs = () => {
        const sl = props.selectedLine || {};
        const isMixed = sl.isMixedGroup || (!!sl.mixedGroupId && Array.isArray(sl.groupItems) && sl.groupItems.length > 0);
        if (isMixed) {
            const total = Number(sl.quantity ?? sl.amount ?? 0) || 0;
            const items = (sl.groupItems || []).map((gi) => ({
                bags: gi.bags,
                bins: gi.bins,
                stockItemId: gi.stockItemId,
                stockItem: gi.stockItem, // if present from inventory normalization
                quantity: Number(gi.portalQuantity ?? gi.quantity ?? 0) || 0,
                mixedGroupId: gi.mixedGroupId || sl.mixedGroupId,
                lotSerialNbr: gi.lotSerialNbr || sl.lotSerialNbr,
                expiryDate: gi.expiryDate || sl.expiryDate,
                supplier: gi.supplier || sl.supplier,
            }));
            // Normalize: if sum of items doesn't match total, scale proportionally
            const sum = items.reduce((p, c) => p + (Number(c.quantity) || 0), 0);
            if (total > 0 && sum > 0 && Math.abs(sum - total) > 0.5) {
                const scale = total / sum;
                items.forEach(it => { it.quantity = Math.round((Number(it.quantity) || 0) * scale); });
                // Fix rounding drift
                let diff = total - items.reduce((p, c) => p + (Number(c.quantity) || 0), 0);
                let idx = 0;
                while (diff !== 0 && idx < items.length) {
                    items[idx].quantity += (diff > 0 ? 1 : -1);
                    diff += (diff > 0 ? -1 : 1);
                    idx++;
                }
            }
            return items.length > 0 ? items : [{ bags: sl.bags, bins: sl.bins, stockItemId: sl.stockItemId, quantity: total, mixedGroupId: sl.mixedGroupId }];
        }
        return [{
            bags: sl.bags, bins: sl.bins, stockItemId: sl.stockItemId, quantity: (CompletedAndEdit ? sl.quantity : sl.amount),
        }];
    };
    const [oystersIn, setOystersIn] = React.useState(buildInitialInputs());
    const supplier = props.selectedLine ? (CompletedAndEdit ? props.selectedLine.coreInventory?.receival?.supplier?.name : props.selectedLine.supplier) : '';
    const lotNbr = props.selectedLine ? (CompletedAndEdit ? props.selectedLine.coreInventory?.lotSerialNbr : props.selectedLine.lotSerialNbr) : '';
    const [comment, setComment] = React.useState(CompletedAndEdit ? props.selectedLine?.comment : (hasStarted ? props.selectedLine?.processRun?.comment : null));
    const [workers, setWorkers] = React.useState(CompletedAndEdit ? (props.selectedLine?.staff) : (hasStarted ? props.selectedLine?.processRun?.staff : null));
    const processRunId = CompletedAndEdit ? props.selectedLine.id : props.selectedLine.processRunId
    // For mixed groups with multiple active runs, always use the lowest run id when linking outputs
    const outputsRunId = React.useMemo(() => {
        if (!hasStarted) return null;
        const group = props.selectedLine?.groupItems;
        if (Array.isArray(group) && group.length > 0) {
            const ids = group
                .map(g => Number(g?.id ?? g?.processRunId))
                .filter(n => Number.isFinite(n));
            if (ids.length > 0) return Math.min(...ids);
        }
        return Number(processRunId) || null;
    }, [hasStarted, props.selectedLine, processRunId]);

    console.log(props.selectedLine)

    console.log(props.warehouseId)
    React.useEffect(() => {
        getAPIDataReturn('/Processing/GetProductionLines').then((data) => {
            setEquipmentList(data.filter((d) => d.warehouseId === props.warehouseId));
        });
    }, []);
    React.useEffect(() => {
        if (hasStarted) {
            getAPIData('/StockItems/Get', setStockItems);
        }
    }, []);

    React.useEffect(() => {

        //load cache
        if (props.useCache&&!hasStarted) {
            GetAllCacheData(setWorkers, 'noOfWorkers')
        }
        // get production line
        //equipmentList

        ///get existing outputs
    if (props.selectedLine && outputsRunId) {
        getAPIDataParamsReturn('/Processing/GetProcessingOutputs', { ProcessRunId: outputsRunId }).then((data) => {
                var oys = data.map((d) => {
                    return { stockItemId: d.stockItemId, quantity: d.quantity, bins: d.bins, bags: d.noOfUnits, id: d.id }
                });
                setCurrentOutputs(oys);
                setOystersOut(oys.length > 0 ? oys : [{}]);
            }).catch((error) => {
                console.log(error);
                setOystersOut([{}]);
            });
    } else {
            setOystersOut([{}]);
    }
    }, [props.selectedLine, outputsRunId])

    const getVariance = (oysIn, oysOut, perc) => {
        // Sum total input across all input lines (supports mixed groups)
        let totalOystersIn = 0;
        for (let i = 0; i < (oysIn?.length || 0); i++) {
            totalOystersIn += oysIn[i]?.quantity ? Number(oysIn[i].quantity) : 0;
        }
        // Sum total output
        let totalOystersOut = 0;
        for (let i = 0; i < (oysOut?.length || 0); i++) {
            totalOystersOut += oysOut[i]?.quantity ? Number(oysOut[i].quantity) : 0;
        }
        const diff = Number(totalOystersIn) - Number(totalOystersOut);
        if (perc) {
            // Avoid divide-by-zero; if no input, treat variance percent as 0
            return totalOystersIn > 0 ? Math.abs(diff) / Number(totalOystersIn) : 0;
        } else {
            return diff;
        }
    }


    const validate = (finished) => () => {
        var warningMessages = [];
        var errorMessages = [];
        //console.log(finished)
        if (!equipment.allowMultiple) {
            var matchSchedule = scheduleList?.find((s) => s.processRun?.processLineId === equipment.id);
            if (matchSchedule) {
                if (matchSchedule?.id !== props.selectedLine?.id) {
                    errorMessages.push("There is already a process running on this line");
                }
            }
        }
        // Mixed group: validate against sum of inputs and total available on selected line
        if (props.selectedLine?.isMixedGroup || props.selectedLine?.mixedGroupId) {
            const totalIn = oystersIn.reduce((p, c) => p + (Number(c.quantity) || 0), 0);
            if (props.selectedLine?.totalAvailable < totalIn) {
                errorMessages.push("You have entered more oysters than are available in the receival");
            }
        } else {
            if (props.selectedLine?.totalAvailable < oystersIn[0].quantity) {
                errorMessages.push("You have entered more oysters than are available in the receival");
            }
        }
        if (!equipment) {
            errorMessages.push("You must select the line you are using");
        }
        if (!workers || (workers < 0)) {
            errorMessages.push("You must enter the number of workers for this run");

        }
        //cant have duplicate stock items
        var stockItemsids = [];
        for (var i = 0; i < oystersOut.length; i++) {
            if (stockItemsids.includes(oystersOut[i].stockItem?.id)) {
                errorMessages.push("You cannot have duplicate stock items for Output");
            }
            stockItemsids.push(oystersOut[i].stockItem?.id);
        }

        if (!staffMember) {
            errorMessages.push("You must select a Staff Member");

        }
        if (props.selectedLine?.isMixedGroup || props.selectedLine?.mixedGroupId) {
            // For mixed group: don't require per-line edits; just ensure total > 0 and stock items exist
            const totalIn = oystersIn.reduce((p, c) => p + (Number(c.quantity) || 0), 0);
            if (!(totalIn > 0)) {
                errorMessages.push("You must enter a total quantity for input");
            }
            if (oystersIn.some(oi => !oi.stockItem && !oi.stockItemId)) {
                errorMessages.push("Missing stock item on a grouped line");
            }
        } else {
            for (var i = 0; i < oystersIn.length; i++) {
                if (!oystersIn[i].quantity || (oystersIn[i].quantity <= 0)) {
                    errorMessages.push("You must enter a quantity of oysters for input");
                }
                if (!oystersIn[i].stockItem) {
                    errorMessages.push("You must select a stockitem for input");
                }
            }
        }
    if (hasStarted) {
            for (var i = 0; i < oystersOut.length; i++) {
                if (!oystersOut[i].quantity || (oystersOut[i].quantity <= 0)) {
                    errorMessages.push("You must enter a quantity of oysters for output");
                }
                if (!oystersOut[i].stockItem) {
                    errorMessages.push("You must select a stockitem for output");
                }
            }
        }
        if (finished) {
            var gradeNTotal = [];
            for (var j = 0; j < oystersOut.length; j++) {
                var gradeOrder = (stockItems.find((s) => (s.id === (oystersOut[j].stockItem?.id))))?.grade?.sortOrder;
                var index = gradeNTotal.findIndex((g) => g.grade === gradeOrder);
                if (index >= 0) {
                    gradeNTotal[index].total += oystersOut[j].quantity;
                } else {
                    gradeNTotal.push({ grade: gradeOrder, total: oystersOut[j].quantity });
                }
            }
            var showApprovalButton = errorMessages.length === 0; //if no other errors and then there are errors below it will show approval button
            if (Array.isArray(stockItems) && stockItems.length > 0) {
                // When there are multiple inputs, compare outputs outside the input grade band
                const inputGrades = oystersIn
                    .map(oi => (stockItems.find(s => s.id === oi.stockItem?.id)?.grade?.sortOrder))
                    .filter(g => Number.isFinite(g));

                if (inputGrades.length > 0) {
                    const minInGrade = Math.min(...inputGrades);
                    const maxInGrade = Math.max(...inputGrades);
                    const totalInputQty = oystersIn.reduce((p, c) => p + (Number(c?.quantity) || 0), 0);

                    // Totals of outputs above/below the input grade band
                    const above = gradeNTotal.filter(g => Number.isFinite(g.grade) && g.grade > maxInGrade);
                    const below = gradeNTotal.filter(g => Number.isFinite(g.grade) && g.grade < minInGrade);

                    const totalAbove = above.reduce((p, c) => p + (Number(c.total) || 0), 0);
                    const totalBelow = below.reduce((p, c) => p + (Number(c.total) || 0), 0);

                    const maxAboveGrade = above.length ? Math.max(...above.map(x => x.grade)) : null;
                    const minBelowGrade = below.length ? Math.min(...below.map(x => x.grade)) : null;

                    const applyBandCheck = (direction, dist, variance) => {
                        if (!Number.isFinite(dist) || dist <= 0) return;
                        // thresholds by supplier internal/external and grade distance
                        let warnVar = 0.05, errorVar = 0.07, gradDistance = '';
                        if (dist > 2) {
                            gradDistance = 'three grades';
                            if (props.selectedLine?.supplierObject?.isInternal) { warnVar = 0.07; errorVar = 0.15; } else { warnVar = 0.05; errorVar = 0.125; }
                        } else if (dist > 1) {
                            gradDistance = 'two grades';
                            if (props.selectedLine?.supplierObject?.isInternal) { warnVar = 0.10; errorVar = 0.20; } else { warnVar = 0.07; errorVar = 0.15; }
                        } else if (dist > 0) {
                            if (props.selectedLine?.supplierObject?.isInternal) { warnVar = 0.20; errorVar = 0.30; } else { warnVar = 0.10; errorVar = 0.20; }
                        }
                        if (variance > errorVar) {
                            errorMessages.push(`You are ${direction} ${gradDistance} more than ${(errorVar * 100).toFixed(0)}%! Seek Approval!`);
                        } else if (variance > warnVar) {
                            warningMessages.push(`You are ${direction} ${gradDistance} more than ${(warnVar * 100).toFixed(0)}%! Are you sure this is correct?`);
                        }
                    };

                    // Above band (upgrading)
                    if (totalInputQty > 0 && totalAbove > 0 && Number.isFinite(maxAboveGrade)) {
                        const distAbove = Math.abs(maxAboveGrade - maxInGrade);
                        const varianceAbove = Math.min(1, totalAbove / totalInputQty);
                        applyBandCheck('upgrading', distAbove, varianceAbove);
                    }

                    // Below band (downgrading)
                    if (totalInputQty > 0 && totalBelow > 0 && Number.isFinite(minBelowGrade)) {
                        const distBelow = Math.abs(minInGrade - minBelowGrade);
                        const varianceBelow = Math.min(1, totalBelow / totalInputQty);
                        applyBandCheck('downgrading', distBelow, varianceBelow);
                    }

                    // If only a single input, also keep per-line checks for closer comparisons
                    if (oystersIn.length === 1) {
                        for (var j = 0; j < gradeNTotal.length; j++) {
                            var oyInGradeSingle = stockItems.find((s) => s.id === oystersIn[0].stockItem?.id)?.grade?.sortOrder;
                            var oyOutGradeSingle = gradeNTotal[j].grade;
                            var varianceSingle = 1 - (Math.abs(oystersIn[0].quantity - gradeNTotal[j].total) / oystersIn[0].quantity);
                            var directionSingle = 'downgrading';
                            if (oyInGradeSingle < oyOutGradeSingle) { directionSingle = 'upgrading'; }
                            let warnVarS = 0.05, errorVarS = 0.07, changeInGradeS = false, gradDistanceS = '';
                            const distS = Math.abs(oyInGradeSingle - oyOutGradeSingle);
                            if (distS > 2) { changeInGradeS = true; gradDistanceS = 'three grades'; if (props.selectedLine?.supplierObject?.isInternal) { warnVarS = 0.07; errorVarS = 0.15; } else { warnVarS = 0.05; errorVarS = 0.125; } }
                            else if (distS > 1) { changeInGradeS = true; gradDistanceS = 'two grades'; if (props.selectedLine?.supplierObject?.isInternal) { warnVarS = 0.10; errorVarS = 0.20; } else { warnVarS = 0.07; errorVarS = 0.15; } }
                            else if (distS > 0) { changeInGradeS = true; if (props.selectedLine?.supplierObject?.isInternal) { warnVarS = 0.20; errorVarS = 0.30; } else { warnVarS = 0.10; errorVarS = 0.20; } }
                            if (changeInGradeS) {
                                if (varianceSingle > errorVarS) { errorMessages.push("You are " + directionSingle + " " + gradDistanceS + " more than " + ((errorVarS * 100).toFixed(0)) + "%! Seek Approval!"); }
                                else if (varianceSingle > warnVarS) { warningMessages.push("You are " + directionSingle + " " + gradDistanceS + " more than " + ((warnVarS * 100).toFixed(0)) + "%! Are you sure this is correct?"); }
                            }
                        }
                    }
                }
            }
            var totalOystersOut = 0;
            for (var i = 0; i < oystersOut.length; i++) {
                totalOystersOut += oystersOut[i].quantity;
            }
            if (getVariance(oystersIn, oystersOut, true) > 0.1) {
                warningMessages.push("Variance is greater than 10%! Are you sure this is correct?");
            }
            if (getVariance(oystersIn, oystersOut, true) >= 0.15) {
                errorMessages.push("Variance is greater than 15%! This cannot be completed. Please see a manager.");
            }
        }


        const formatErrorMessage = (array) => {
            var errString = '';
            array.map((a) => {
                errString = errString + a + '<br/>';
            });
            return errString;
        }
        if (errorMessages.length > 0) {
            if (showApprovalButton) {
                Swal.fire({
                    html: formatErrorMessage(errorMessages),
                    icon: 'error',
                    title: 'Oops...',
                    showCancelButton: true,
                    confirmButtonText: 'Approve this Variance',
                    cancelButtonText: 'Edit Values',
                }).then(result => {
                    if (result.value) {
                        if (checkAccess(props.access, requiredAdminAccess)) {
                            submit(finished);
                        } else {
                            let pin = prompt("Please enter your admin PIN");
                            if (pin.toLowerCase() === 'freeoysters') {
                                submit(finished);
                            }
                        }
                    } else if (result.dismiss === Swal.DismissReason.cancel) {

                    }
                });
            } else {
                Swal.fire({
                    html: formatErrorMessage(errorMessages),
                    icon: 'error',
                    title: 'Oops...',

                });
            }
        } else if (warningMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(warningMessages),
                icon: 'warning',
                title: 'Oops...',
                showCancelButton: true,
                confirmButtonText: 'Yes, Continue',
                cancelButtonText: 'No, I\'ll edit it',

            }).then(result => {
                if (result.value) {
                    submit(finished);
                } else if (result.dismiss === Swal.DismissReason.cancel) {

                }
            });
        } else {
            submit(finished);
        }
    }

    const submit = async (finished) => {
        var respArr = [];
        setError(null);
        setLoading(true);
        try {
        if (hasStarted) {
            //update input
            console.log(editInput)
            if (editInput) {
                // If this is a mixed group, update all underlying process runs; otherwise update the single run
                if (props.selectedLine?.isMixedGroup || props.selectedLine?.mixedGroupId) {
            console.log(props.selectedLine?.groupItems)
                    const group = Array.isArray(props.selectedLine?.groupItems) ? props.selectedLine.groupItems : [];
                    const updates = await Promise.all(group.map(async (gi) => {
                        const runId = Number(gi?.id ?? gi?.processRunId);
                        // Match input line by stock item id
                        const giStockId = gi?.stockItemId ?? gi?.stockItem?.id;
                        const oi = (oystersIn || []).find(x => (x?.stockItem?.id ?? x?.stockItemId) === giStockId);
                        const params = {
                            ProcessRunId: runId,
                            ProductionLineId: equipment.id,
                            Amount: Number(oi?.quantity ?? gi?.portalQuantity ?? gi?.quantity ?? 0),
                            Bins: Number(oi?.bins ?? gi?.bins ?? 0),
                            Bags: Number(oi?.bags ?? gi?.bags ?? 0),
                            Staff: workers,
                            StaffMemberId: staffMember.id
                        };
                        return postApiOrThrow('/Processing/UpdateProcess', params);
                    }));
                    respArr = [...respArr, ...updates];
                } else {
                    const params = {
                        ProcessRunId: processRunId,
                        ProductionLineId: equipment.id,
                        Amount: oystersIn[0].quantity,
                        Bins: oystersIn[0].bins,
                        Bags: oystersIn[0].bags,
                        Staff: workers,
                        StaffMemberId: staffMember.id
                    };
                    respArr.push(await postApiOrThrow('/Processing/UpdateProcess', params));
                }
            }
            var prmArr = [];
            for (var i = 0; i < currentOutputs.length; i++) {
                if (!oystersOut.find((x) => x.stockItem.id === currentOutputs[i].stockItemId)) {
                    params = {
                        ProcessRunId: outputsRunId,
                        StockItemId: currentOutputs[i].stockItemId,
                        Amount: 0,
                        NoOfUnits: 0,
                    }
                    prmArr.push(await postApiOrThrow('/Processing/AddUpdateProcessingOutput', params));
                }
            }
            await Promise.all(prmArr);
            respArr = [...respArr, ...prmArr];
            //update output
            //update add output
            prmArr = [];
            var params = {};
        for (var i = 0; i < oystersOut.length; i++) {
                params = {
            ProcessRunId: outputsRunId,
                    StockItemId: oystersOut[i].stockItem.id,
                    NoOfUnits: oystersOut[i].bags ? oystersOut[i].bags : 0,
                    Amount: oystersOut[i].quantity,
                }
                prmArr.push(await postApiOrThrow('/Processing/AddUpdateProcessingOutput', params));
            }
            await Promise.all(prmArr);
            respArr = [...respArr, ...prmArr];

            // is it finished?
            if (finished) {
                if (props.selectedLine?.isMixedGroup || props.selectedLine?.mixedGroupId) {
                    // Call /Processing/EndProcessingMixed with MixedGroupId
                    const mixedGroupId = props.selectedLine?.mixedGroupId || (oystersIn.find(x => x.mixedGroupId)?.mixedGroupId);
                    if (mixedGroupId) {
                        const endMixed = await postApiOrThrow('/Processing/EndProcessingMixed', { MixedGroupId: mixedGroupId });
                        respArr.push(endMixed);
                    }
                } else {
                    // Single: call /Processing/EndProcessing as before
                    if (Number.isFinite(Number(processRunId))) {
                        const endSingle = await postApiOrThrow('/Processing/EndProcessing', { ProcessRunId: Number(processRunId) });
                        respArr.push(endSingle);
                    }
                }
            }
            if (comment) {
                const commentprm = {
                    ProcessRunId: processRunId,
                    Comment: comment,
                }
                respArr.push(await postApiOrThrow('/Processing/AddComment', commentprm));
            }
        } else {
            // Starting processing: if mixed group, start a run per line and include MixedGroupId; else single as before
            if (props.selectedLine?.isMixedGroup || props.selectedLine?.mixedGroupId) {
                const mgid = props.selectedLine?.mixedGroupId || (oystersIn.find(x => x.mixedGroupId)?.mixedGroupId);
                const procMGid = uuidv4();
                for (let i = 0; i < oystersIn.length; i++) {
                    const oi = oystersIn[i];
                    const stockId = oi.stockItem?.id || oi.stockItemId;
                    const params = {
                        ExpiryDate: textfieldDateFormat(new Date(oi.expiryDate || props.selectedLine.expiryDate)),
                        LotSerialNbr: oi.lotSerialNbr || props.selectedLine.lotSerialNbr,
                        ProductionLineId: equipment.id,
                        StockItemId: stockId,
                        Amount: oi.quantity,
                        Bins: oi.bins ? oi.bins : 0,
                        Bags: oi.bags ? oi.bags : 0,
                        Staff: workers,
                        StaffMemberId: staffMember.id,
                        InvMixedGroupId: mgid || null,
                        ProcMixedGroupId: procMGid || null

                    };
                    respArr.push(await postApiOrThrow('/Processing/StartProcessing', params));
                }
            } else {
                const params = {
                    ExpiryDate: textfieldDateFormat(new Date(props.selectedLine.expiryDate)),
                    LotSerialNbr: props.selectedLine.lotSerialNbr,
                    ProductionLineId: equipment.id,
                    StockItemId: oystersIn[0].stockItem.id,
                    Amount: oystersIn[0].quantity,
                    Bins: oystersIn[0].bins ? oystersIn[0].bins : 0,
                    Bags: oystersIn[0].bags ? oystersIn[0].bags : 0,
                    Staff: workers,
                    StaffMemberId: staffMember.id,
                    InvMixedGroupId: props.selectedLine?.mixedGroupId || null,

                };
                respArr.push(await postApiOrThrow('/Processing/StartProcessing', params));
            }
        }
        if (respArr.reduce((p, c) => (p && c.ok), true)) {
            setLoading(false);
            setCompleted(true);
            setUpdateCompleted(true);
        } else {
            throw new Error("Failed to submit");
        }
        } catch (submitError) {
            setLoading(false);
            setError(submitError?.message || "Failed to submit");
        }
    }
    const enableInputEdit = () => {
        setEditInput(true);
    }
    const remSR = (name) => {
        if (name && name.description) {
            return name.description.replace('Sydney Rock', '');
        } else {
            return '';
        }
    }
    const deleteCurrentRun = () => {
        setShowDelete(true);
        setEditBench(props.selectedLine?.processRun || props.selectedLine);

    }
    const handleCloseDelete = () => {
        setShowDelete(false);
        props.handleClose();
    }
    const updateWorkers = (val) => {
        
        if (props.useCache) {
            addToCache(val, 'noOfWorkers');
        }
        setWorkers(val);
    }
    const staffMemId = CompletedAndEdit ? props.selectedLine?.staffMemberId : (hasStarted ? props.selectedLine?.processRun?.staffMemberId : null);
    const procLineId = CompletedAndEdit ? props.selectedLine?.processLineId : (hasStarted ? props.selectedLine?.processRun?.processLineId : null);
    console.log(props.onlyLive);
    return (
        <Box
            component="form"
            sx={{
                '& .MuiTextField-root': { m: 1, width: '24ch' },
            }}
            noValidate
            autoComplete="off"
        >
            {checkAccess(props.access, requiredMYOBAccess) ? <FormModal open={showDelete} handleClose={handleCloseDelete} title={"Delete Current Process"} noWidth >
                <DeleteCurrentRun outputs={{ ...editBench, processRunOutputs: currentOutputs }} access={props.access} handleClose={handleCloseDelete} selectedBench={{...props.selectedLine,id:processRunId,staffMemberId:staffMemId,processLineId:procLineId,staff:workers}} warehouseId={props.warehouse?.id} warehouse={props.warehouse} />
            </FormModal > : null}
            {props.selectedLine?.start&&checkAccess(props.access, requiredMYOBAccess) ?<IconButton aria-label="Delete process run" style={{ paddingBottom: '15px', position: 'absolute', top: '5%', right: '1%' }} variant={'h3'} onClick={() => { deleteCurrentRun() }}>< DeleteIcon /></IconButton>: null}
            <Div>

                <TextField
                    disabled
                    id="outlined-required"
                    label="Supplier"
                    value={supplier}
                />
                <TextField
                    disabled
                    id="outlined-required"
                    label="Lot Nbr"
                    value={lotNbr}
                />
            </Div>
            <Div>
                {/*//comments*/}
                {/*//processing time*/}
                {/*//no of staff on line*/}

                <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <StaffMember useCache={!staffMemId} staffId={staffMemId} driver={staffMember} setDriver={setStaffMember} disabled={(hasStarted && !editInput) || CompletedSoDisabled} noTitle />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                        <SelectTextField key={'si' + props.index} checkID={procLineId} title="Select Line" state={equipment} nameFunc={(n) => (n.description)} list={props.onlyLive ? equipmentList.filter((el) => (!el.isHalfshell)) : equipmentList} setState={setEquipment} disabled={(hasStarted && !editInput) || CompletedSoDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                        <ControlledTextField key={'bg' + props.index} title="Number of Workers" state={workers} setState={updateWorkers} disabled={(hasStarted && !editInput) || CompletedSoDisabled} number />
                    </Grid>
                    {hasStarted ? <Grid item xs={12} >
                        <TextField
                            disabled={CompletedSoDisabled}
                            id="outlined-multiline-static"
                            label="Comment"
                            value={comment}
                            onChange={(event) => { setComment(event.target.value) }}
                            multiline
                            InputLabelProps={{
                                shrink: true,
                            }}
                            rows={4} />
                    </Grid> : ''}
                    <Grid item xs={12} >
                        {/*Icon button edit */}


                        <Typography variant={'h3'}>{"Input Stock"}{hasStarted ? <IconButton aria-label="cci" onClick={CompletedSoDisabled ? () => { } : enableInputEdit}>
                            <EditIcon />
                        </IconButton> : null}</Typography>
                        <OystersGroup
                            allStock={props.WIPcode || props.allStock}
                            processed={props.processed}
                            disableStockItem
                            oysters={oystersIn}
                            starter={false}
                            setOysters={setOystersIn}
                            disabled={(hasStarted && !editInput) || CompletedSoDisabled}
                            disableGroupPercent
                            lockGroupLines
                            disableSplitRemove
                            allowSplit
                            noTitle
                        />
                    </Grid>

                    {hasStarted ? <Grid item xs={12} >
                        <Typography variant={'h3'}>{"Output Stock"}</Typography>
                            
                        <OystersGroup showAsUnits oysters={oystersOut} setOysters={setOystersOut} inputStockItemId={initialInputStockItemId} disabled={CompletedSoDisabled} noTitle WIPcode={props.WIPcode} processed processRunId={outputsRunId} nameFunc={remSR} disableSplitRemove/>
                    </Grid> : null}

                    {CompletedAndEdit ?
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            {/*<FormGroup>*/}
                            {/*    <FormControlLabel control={<Checkbox disabled={completed} checked={finished} onChange={(event) => {setFinished(event.target.checked);}} />} label="Process Completed" />*/}
                            {/*</FormGroup>*/}
                            <div style={{ paddingTop: '5px', textAlign: 'left' }}>
                                <SubmitButtonDisplayer title={'Update'} completed={updateComplete} error={error} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate(false)} handleClose={props.handleClose} />
                            </div>
                        </Grid>
                        : (hasStarted ? <React.Fragment>
                            <Grid item xs={12} >
                                <Typography variant={'h3'}> {"Current Variance: " + numberFormatwithNeg((getVariance(oystersIn, oystersOut, true) * 100)?.toFixed(0)) + '% (' + numberFormatwithNeg(getVariance(oystersIn, oystersOut, false)) + ' doz)'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                {/*<FormGroup>*/}
                                {/*    <FormControlLabel control={<Checkbox disabled={completed} checked={finished} onChange={(event) => {setFinished(event.target.checked);}} />} label="Process Completed" />*/}
                                {/*</FormGroup>*/}
                                <div style={{ paddingTop: '5px', textAlign: 'left' }}>
                                    <SubmitButtonDisplayer title={'Add Output'} completed={CompletedSoDisabled} error={error} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate(false)} handleClose={props.handleClose} />
                                </div>
                            </Grid>
                            {stockItems ? <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                {/*<FormGroup>*/}
                                {/*    <FormControlLabel control={<Checkbox disabled={completed} checked={finished} onChange={(event) => {setFinished(event.target.checked);}} />} label="Process Completed" />*/}
                                {/*</FormGroup>*/}
                                <div style={{ paddingTop: '5px', textAlign: 'left' }}>
                                    <SubmitButtonDisplayer title={'Add Output and Complete'} completed={CompletedSoDisabled} error={error} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate(true)} handleClose={props.handleClose} />
                                </div>
                            </Grid> : null}
                        </React.Fragment> :
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                {/*<FormGroup>*/}
                                {/*    <FormControlLabel control={<Checkbox disabled={completed} checked={finished} onChange={(event) => {setFinished(event.target.checked);}} />} label="Process Completed" />*/}
                                {/*</FormGroup>*/}
                                <div style={{ paddingTop: '5px', textAlign: 'left' }}>
                                    <SubmitButtonDisplayer title={'Start Processing'} completed={CompletedSoDisabled} error={error} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate(false)} handleClose={props.handleClose} />
                                </div>
                            </Grid>)}
                </Grid>
            </Div>
        </Box >
    );
};


export default ReceivalForm;


